import { Controller } from "@hotwired/stimulus"
import { renderStreamMessage } from "@hotwired/turbo"
import {
  applyPdfPageCount,
  detectCategoryFromFilename,
  directUpload,
  updateUploadedFileLink,
} from "./active_storage/upload_helpers"

function nextAnimationFrame() {
  return new Promise((resolve) => requestAnimationFrame(resolve))
}

async function waitForElement(getElement, { attempts = 15 } = {}) {
  for (let attempt = 0; attempt < attempts; attempt += 1) {
    const element = getElement()
    if (element) return element
    await nextAnimationFrame()
  }

  return null
}

// Batch upload into nested form rows via Turbo Stream nested #new.
export default class extends Controller {
  static targets = [
    "input",
    "trigger",
    "progressContainer",
    "progressBar",
    "controls",
    "statusText",
    "cancelButton",
  ]
  static values = {
    url: String,
    newJobFileUrl: String,
    listId: String,
  }

  connect() {
    if (!this.hasInputTarget) return

    this.cancelled = false
    this.inputTarget.addEventListener("change", this.handleFileSelect.bind(this))
  }

  handleFileSelect(event) {
    const files = Array.from(event.target.files)
    if (files.length === 0) return

    this.cancelled = false
    void this.uploadFiles(files)
  }

  cancelUpload() {
    this.cancelled = true
    if (this.hasStatusTextTarget) {
      this.statusTextTarget.textContent = "Upload cancelled."
    }
  }

  async uploadFiles(files) {
    this.showUploadUi()

    for (const file of files) {
      if (this.cancelled) break

      const delegate = {
        directUploadWillStoreFileWithXHR: (request) => {
          request.upload.addEventListener("progress", (event) => {
            const progress = Math.round((event.loaded / event.total) * 100)
            if (this.hasProgressBarTarget) {
              this.progressBarTarget.style.width = `${progress}%`
            }
            if (this.hasStatusTextTarget) {
              this.statusTextTarget.textContent = `Uploading ${file.name} (${progress}%)`
            }
          })
        },
      }

      try {
        if (this.hasStatusTextTarget) {
          this.statusTextTarget.textContent = `Preparing to upload ${file.name}...`
        }
        if (this.hasProgressBarTarget) {
          this.progressBarTarget.style.width = "0%"
        }

        const newField = await this.appendNestedLineViaTurbo()
        const blob = await directUpload(file, this.urlValue, delegate)

        this.applyCategoryFromFilename(newField, file.name)

        const hiddenInput = newField.querySelector('[data-active-storage-compact-target="hiddenInput"]')
        if (hiddenInput) {
          hiddenInput.value = blob.signed_id
        } else {
          console.error("active-storage-multiple: hidden input missing on appended row")
        }

        const linkContainer = newField.querySelector('[data-active-storage-compact-target="linkContainer"]')
        updateUploadedFileLink(linkContainer, blob)

        await applyPdfPageCount(blob.signed_id, file, newField)
      } catch (error) {
        if (this.hasStatusTextTarget) {
          this.statusTextTarget.textContent = `Failed to upload ${file.name}.`
        }
        console.error(error)
        break
      }
    }

    if (!this.cancelled && this.hasStatusTextTarget) {
      this.statusTextTarget.textContent = "Upload complete."
    }

    setTimeout(() => this.resetUploadUi(), 2000)
  }

  showUploadUi() {
    if (this.hasTriggerTarget) this.triggerTarget.classList.add("hidden")
    if (this.hasProgressContainerTarget) this.progressContainerTarget.classList.remove("hidden")
    if (this.hasControlsTarget) this.controlsTarget.classList.remove("hidden")
  }

  resetUploadUi() {
    if (this.hasTriggerTarget) this.triggerTarget.classList.remove("hidden")
    if (this.hasProgressContainerTarget) this.progressContainerTarget.classList.add("hidden")
    if (this.hasControlsTarget) this.controlsTarget.classList.add("hidden")
    if (this.hasInputTarget) this.inputTarget.value = ""
  }

  async appendNestedLineViaTurbo() {
    if (!this.hasNewJobFileUrlValue) {
      throw new Error("active-storage-multiple requires newJobFileUrl")
    }

    const response = await fetch(this.newJobFileUrlValue, {
      method: "GET",
      credentials: "same-origin",
      headers: {
        Accept: "text/vnd.turbo-stream.html",
      },
    })

    if (!response.ok) {
      throw new Error(`active-storage-multiple: nested #new failed (${response.status})`)
    }

    const html = await response.text()
    if (!html.includes("<turbo-stream")) {
      throw new Error("active-storage-multiple: nested #new returned a non-turbo-stream response")
    }

    const frameId = html.match(/<turbo-frame id="([^"]+)"/)?.[1]

    renderStreamMessage(html)

    const line = await waitForElement(() => {
      if (frameId) {
        return document.getElementById(frameId)?.querySelector('[data-nested-line-list-target="line"]')
      }

      const list = this.nestedLineList
      if (!list) return null

      const lines = list.querySelectorAll('[data-nested-line-list-target="line"]')
      return lines[lines.length - 1] ?? null
    }, { attempts: 30 })

    if (!line) {
      throw new Error("active-storage-multiple: appended row not found")
    }

    return line
  }

  get nestedLineList() {
    if (this.hasListIdValue) {
      return document.getElementById(this.listIdValue)
    }

    return this.element.previousElementSibling
  }

  applyCategoryFromFilename(fieldElement, filename) {
    const categorySelect = fieldElement.querySelector('select[name*="[category]"]')
    if (!categorySelect) return

    const category = detectCategoryFromFilename(filename)
    if ([...categorySelect.options].some((option) => option.value === category)) {
      categorySelect.value = category
    }
  }
}
